import http from '@/config/api';
import router from '@/router';
import { generateRandomString } from '@/helpers/common';

export default {
  async login({ commit }, payload) {
    const salt = btoa(payload.clientId + ',' + new Date().getTime());
    const directory = payload?.directory || router.currentRoute.value.params.directory;
    const res = await http
      .post('token', {
        ...payload,
        grantType: 'client_credentials',
        salt: salt,
      }, {
        params: {
          directory,
        },
      })
      .then((response) => {
        const { accessToken, refreshToken } = response.data;
        const domain = process.env.VUE_APP_DOMAIN;
        const refreshTokenInfo = { salt, refreshToken, domain };
        commit('SET_TOKEN', accessToken);
        commit('SET_REFRESH_TOKEN', JSON.stringify(refreshTokenInfo));
      })
      .catch((err) => ({
        ...err,
      }));
    return res;
  },
  async getUserInfor({ commit }, payload) {
    const directory = payload?.directory || router.currentRoute.value?.params.directory;
    return await http.get('/user', {
      params: {
        directory,
        isHome: payload?.isHome
      },
    }).then((response) => {
      commit('SET_INFOR', response?.data);
      return response?.data;
    });
  },
  async acceptPolicy({ commit }, payload) {
    const directory = payload?.directory || router.currentRoute.value?.params.directory;
    return await http.post(`event/${directory}/use`, payload, { closeTermsOfService: true }).then((response) => {
      if (response) {
        commit('SET_INFOR', { isAgreed: true });
        return response;
      }
    });
  },
  async logout({ dispatch }) {
    await http.delete('/logout').then(async () => {
      await dispatch('resetAuth');
    });
  },
  async refreshToken({ commit }) {
    let encodedRefreshToken = localStorage.getItem('refreshToken') || '';
    let refreshTokenInfo = {};
    let tmpString = '';
    if (encodedRefreshToken) {
      refreshTokenInfo = JSON.parse(atob(encodedRefreshToken.slice(10)));
      tmpString = generateRandomString(10) + btoa(Object.values(refreshTokenInfo).join());
    } else return Promise.reject();
    await http
      .post(
        'token',
        {
          grantType: 'client_refresh_token',
          refreshToken: tmpString,
        },
        { notRefresh: true }
      )
      .then((response) => {
        const { accessToken, refreshToken } = response.data;
        refreshTokenInfo.refreshToken = refreshToken;
        commit('SET_TOKEN', accessToken);
        commit('SET_REFRESH_TOKEN', JSON.stringify(refreshTokenInfo));
      });
  },
  async resetPassword(_, email) {
    const res = await http.post('/user/forgotPassword', email).then(
      (res) => res.data,
      (err) => ({
        error: {
          ...err,
        },
      })
    );
    return res;
  },
  async setNewPassword(_, payload) {
    const res = await http
      .post(`/user/password/${payload.decodedToken[2]}`, {
        password: payload.password,
        method: payload.method,
        verify: {
          string: payload.decodedToken[1],
          code: payload.decodedToken[0],
        },
      })
      .then(
        (res) => res.data,
        (err) => ({
          error: {
            ...err,
          },
        })
      );
    return res;
  },
  async checkAuthentication({ commit }, payload) {
    const { key, checkApi } = payload.useAuthentication;
    await http.post('', {
      id: payload.id,
      pass: payload.pass,
    }, {
      baseURL: checkApi,
      headers: {
        'X-Api-Key': key,
      },
      hasCustomConfig: true,
      authenticationApi: true,
    }).then((response) => {
      commit('SET_AUTHENTICATION_TOKEN', response.data?.access_token);
      commit('SET_MAXIMUM_AMOUNT', response.data.addition?.maximumAmount);
    });
  },
  async checkAuthenticationToken({ getters }, payload) {
    const { key, invalidApi } = payload.useAuthentication;
    const token = getters.authenticationToken;
    await http.post('', null, {
      baseURL: invalidApi,
      headers: {
        'X-Api-Key': key,
        Authorization: `Bearer ${token}`,
      },
      hasCustomConfig: true,
      authenticationApi: true,
    });
  },
  async invalidAuthenticationData({ getters, commit }, payload) {
    const { key, invalidApi } = payload.useAuthentication;
    const token = getters.authenticationToken;
    await http.post('', {
      invalid: 1,
    }, {
      baseURL: invalidApi,
      headers: {
        'X-Api-Key': key,
        Authorization: `Bearer ${token}`,
      },
      hasCustomConfig: true,
      authenticationApi: true,
    }).then(() => {
      commit('RESET_AUTHENTICATION_TOKEN');
    });
  },
  resetAuth({ commit }) {
    commit('charge/REMOVE_MONEY_NUM', null, { root: true });
    commit('ticket/RESET_CHARGE_TICKET_LIST', null, { root: true });
    commit('charge/REMOVE_CHARGE_OPTIONS', null, { root: true });
    if (router.currentRoute.value?.name !== 'EventList') {
      commit('event/RESET_PARENT_EVENT_INFO', null, { root: true });
    }
    commit('SET_TOKEN', '');
    commit('SET_REFRESH_TOKEN', '');
    commit('SET_INFOR', {});
    commit('common/SET_BACK_ROUTE_NAME', null, { root: true });
    commit('event/REMOVE_BLOCK_USER', null, { root: true });
    sessionStorage.removeItem('qrData');
    localStorage.removeItem('redirectToMail');
    localStorage.removeItem('backToTop');
    sessionStorage.removeItem('registId');
    sessionStorage.removeItem('mobilephone');
    sessionStorage.removeItem('isLoginBack');
    sessionStorage.removeItem('isClearRegistInfo');
    sessionStorage.removeItem('redirectHome');
    sessionStorage.removeItem('isBarcodePay');
    sessionStorage.removeItem('isSoundOn');
    sessionStorage.removeItem('isQRShop');
    sessionStorage.removeItem('memoData');
    sessionStorage.removeItem('paymentStartTimestamp');
    localStorage.removeItem('CONFIRM_3DS2_ACCESS_ID_INFO');
  },
  createBiometricToken({ commit }, email) {
    commit('SET_BIOMETRIC_TOKEN', '');
    return http
      .post('/user/biomatric/create', {
        email: email || localStorage.getItem('clientId'),
      }).then((response) => {
        commit('SET_BIOMETRIC_TOKEN', response.data?.token_biomatric);
      }).catch(() => {
        commit('SET_BIOMETRIC_TOKEN', '');
      });
  },
  loginWithBiometricToken({ commit }, payload) {
    const salt = btoa(payload.email + ',' + new Date().getTime());
    return http
      .post('/user/biomatric/auth', {
        email: payload.email,
        token_biomatric: payload.biometricToken
      }).then((response) => {
        const { accessToken, refreshToken } = response.data;
        const domain = process.env.VUE_APP_DOMAIN;
        const refreshTokenInfo = { salt, refreshToken, domain };
        commit('SET_TOKEN', accessToken);
        commit('SET_REFRESH_TOKEN', JSON.stringify(refreshTokenInfo));
      }).catch(() => {
        commit('SET_BIOMETRIC_TOKEN', '');
        localStorage.removeItem('clientId');
      });
  },
  tradeWithBiometricToken({ commit }, payload) {
    return http
      .post('/user/biomatric/passcode', {
        email: payload.email,
        token_biomatric: payload.biometricToken
      }).then((response) => {
        return response?.data?.passcode;
      }).catch(() => {
        commit('SET_BIOMETRIC_TOKEN', '');
        return null;
      });
  },
};
